import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, Watch } from '@stencil/core';
import { GoogleMapsApi } from '~service/google-maps/google-maps-api';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';

declare const google: any;

enum TimedMarkerQueueMessageTypes {
  Create,
  Remove,
}

interface TimedMarkerQueueMessage {
  type: TimedMarkerQueueMessageTypes;
  payload: any;
  resolve: Function;
}

@Component({
  tag: 'uvex-google-map',
  styleUrl: 'google-map.scss',
})
export class GoogleMap {
  @Element() el: HTMLElement;
  @Prop() disabled = false;
  @Prop() options = {};
  @Prop() cluster = false;

  @Event() mapsZoomChanged: EventEmitter;
  @Event() mapsBoundsChanged: EventEmitter;
  @Event() mapsCenterChanged: EventEmitter;

  protected _clusterer: MarkerClusterer;

  protected _map: google.maps.Map = null;

  protected knownCoordinates: { lat: number; lng: number; id: string }[] = [];

  protected fitBoundsDebounce?;

  protected boundLiteral?: google.maps.LatLngBoundsLiteral;

  private timedMarkerQueueTimeoutHandler?;
  private timedMarkerQueue: TimedMarkerQueueMessage[] = [];

  private mapOptions = {
    visible: false,
    center: {
      lat: 49.449102,
      lng: 11.0873543,
    },
    options: {
      streetViewControl: false,
      fullscreenControl: false,
      maxZoom: 17,
      minZoom: 2,
      mapTypeControl: false,
      panControl: true,
      panControlOptions: {
        position: 9,
      },
      zoomControl: true,
      zoomControlOptions: {
        position: 9,
        style: 1,
      },
      fit: false,
    },
    clusterOptions: {
      maxZoom: 12,
    },
    mapId: '4415cbe620ccbd05',
    zoom: 8,
    dragging: false,
  };

  @Listen('window:resize')
  public handleResizeEvent() {
    this.fitBounds(true);
  }

  async componentDidLoad() {
    await this.initMap();
  }

  async initMap() {
    if (this.disabled) {
      return;
    }

    await GoogleMapsApi.load();
    const options = { ...this.mapOptions, ...this.options };
    this._map = new google.maps.Map(this.el.querySelector('.map'), options);
    this._map.addListener('zoom_changed', _ => this.mapsZoomChanged.emit(this._map.getZoom()));
    this._map.addListener('bounds_changed', _ => this.mapsBoundsChanged.emit(this._map.getBounds()));
    this._map.addListener('center_changed', _ => this.mapsCenterChanged.emit(this._map.getCenter()));

    if (this.cluster) {
      let renderer = {
        render({ count, position }, _, map) {
          const svg = `<svg class="marker-cluster" fill="#cc6616" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
            <circle cx="120" cy="120" opacity="1" r="70" />
            <circle cx="120" cy="120" opacity=".3" r="90" />
            <circle cx="120" cy="120" opacity=".2" r="110" />
            <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle">${count}</text>
            </svg>`;

          const parser = new DOMParser();
          const svgEl = parser.parseFromString(svg, 'image/svg+xml').documentElement;
          svgEl.setAttribute('transform', 'translate(0 25)');

          return new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            title: '',
            content: svgEl,
          });
        },
      };

      this._clusterer = new MarkerClusterer({
        renderer: renderer,
        map: this._map,
        markers: [],
        algorithmOptions: this.mapOptions.clusterOptions,
        algorithm: new SuperClusterAlgorithm({}),
      });
    }
  }

  @Listen('hoverLocationChanged', { target: 'window' })
  public hoverLocationChangedHandler(event) {
    if (!this.cluster) {
      return;
    }
    // @ts-ignore
    clusterLoop: for (const cluster of this._clusterer.clusters) {
      for (const marker of cluster.markers) {
        // @ts-ignore
        const markerContentContainer = marker.targetElement.querySelector('.marker-content-container') as HTMLElement;
        if (markerContentContainer?.dataset?.locationId == event.detail) {
          // @ts-ignore
          cluster.marker?.targetElement?.querySelector('.marker-cluster')?.classList.toggle('hover');
          break clusterLoop;
        }
      }
    }
  }

  @Watch('disabled')
  async disabledChanged() {
    await this.initMap();
  }

  @Method()
  async createMarker(options: google.maps.marker.AdvancedMarkerElementOptions): Promise<google.maps.marker.AdvancedMarkerElement> {
    return new Promise(resolve => {
      this.queueMessage({ type: TimedMarkerQueueMessageTypes.Create, payload: options, resolve });
    });
  }

  @Method()
  async removeMarker(marker: google.maps.marker.AdvancedMarkerElement): Promise<void> {
    return new Promise(resolve => {
      this.queueMessage({ type: TimedMarkerQueueMessageTypes.Remove, payload: marker, resolve });
    });
  }

  private queueMessage(message: TimedMarkerQueueMessage): void {
    this.timedMarkerQueue.push(message);

    clearTimeout(this.timedMarkerQueueTimeoutHandler);

    this.timedMarkerQueueTimeoutHandler = setTimeout(() => {
      for (const message of this.timedMarkerQueue) {
        if (message.type === TimedMarkerQueueMessageTypes.Create) {
          message.resolve(this.executeCreateMarker(message.payload));
        } else if (message.type === TimedMarkerQueueMessageTypes.Remove) {
          this.executeRemoveMarker(message.payload);
          message.resolve();
        }
      }
      if (this.cluster) {
        this._clusterer.render();
      }
      this.timedMarkerQueue = [];
    }, 200);
  }

  private executeCreateMarker(options: google.maps.marker.AdvancedMarkerElementOptions): google.maps.marker.AdvancedMarkerElement {
    const marker = new google.maps.marker.AdvancedMarkerElement(Object.assign({}, { map: this._map }, options));
    if (this.cluster) {
      this._clusterer.addMarker(marker, true);
    }
    return marker;
  }

  private executeRemoveMarker(marker: google.maps.marker.AdvancedMarkerElement): void {
    if (this.cluster) {
      this._clusterer.removeMarker(marker, true);
    }
    marker.map = null;
  }

  @Method()
  async openInfoWindow(infoWindow: google.maps.InfoWindow, marker: google.maps.marker.AdvancedMarkerElement) {
    infoWindow.open(this._map, marker);
  }

  @Method()
  async addToBound(id: string, lat: number, lng: number) {
    this.knownCoordinates = this.knownCoordinates.filter(bound => bound.id !== id);
    this.knownCoordinates.push({
      lat: lat,
      lng: lng,
      id: id,
    });
    await this.fitBounds();
  }

  @Method()
  async removeFromBound(id: string) {
    this.knownCoordinates = this.knownCoordinates.filter(bound => bound.id !== id);
    await this.fitBounds();
  }

  @Method()
  async clearMap() {
    this.knownCoordinates = [];
    if (this.cluster) {
      this._clusterer.clearMarkers();
    }
    Array.from(this.el.querySelectorAll('uvex-google-map-marker')).forEach((el: HTMLUvexGoogleMapMarkerElement) => el.removeMarker());
  }

  @Method()
  async getBounds() {
    return this._map.getBounds();
  }

  @Method()
  async fitBounds(force: boolean = false) {
    if (google.maps === null) {
      return;
    }
    if (force) {
      delete this.boundLiteral;
    }
    if (this.fitBoundsDebounce) {
      clearTimeout(this.fitBoundsDebounce);
    }
    if (this.knownCoordinates.length >= 1) {
      this.fitBoundsDebounce = setTimeout(_ => {
        const bounds = new google.maps.LatLngBounds();
        this.knownCoordinates.forEach(latLng => {
          bounds.extend({
            lat: latLng.lat,
            lng: latLng.lng,
          });
        });
        if (!this.boundLiteral || !bounds.equals(this.boundLiteral)) {
          this.boundLiteral = bounds.toJSON();
          google.maps.event.trigger(this._map, 'resize');
          this._map.fitBounds(bounds);
          this._map.panToBounds(bounds);
        }
      }, 10);
    }
  }

  public render() {
    return (
      <Host>
        <div class="map" />
        <slot />
      </Host>
    );
  }
}
